import BaseService from '@/services/BaseService';
import { ResponseError } from '@/lib/exceptions';
export type ChartFilter = {
  field: string;
  type: 'set' | 'equals' | 'notEqual';
  values?: string[];
  value?: string;
};

export type ChartQueryRequest = {
  dataSource: string;
  xAxis: { field: string; grain: 'month' | 'quarter' | 'year' };
  series: { field: string };
  metric: string;
  filters?: ChartFilter[];
  topN?: number | null;
  extraParams?: Record<string, number>;
  /**
   * When true, each data point also carries the source <c>facilityId</c>, so
   * the client can filter/re-aggregate by facility subset with no refetch.
   * Server-side <c>topN</c> is ignored in this mode — apply it client-side.
   */
  breakoutByFacility?: boolean;
  /**
   * Optional <c>YYYY-MM-DD</c> As Of Date. Applied server-side only for allowlisted
   * tapes (<c>auto</c>, <c>credit_card</c>, consumer unsecured N-MPL / MPL+BNPL,
   * <c>energy_residential</c>, <c>energy_c_i</c>). Ceiling uses each source's
   * dashboard date field (<c>AS_OF_DATE</c> for DV01 dashboards and Energy C&amp;I).
   * Data Tape Tracker still uses <c>TrackerDateField</c> (<c>REPORT_DATE</c> on most DV01 tapes).
   */
  asOfDate?: string;
};

export type ChartDataPoint = {
  xAxis: string;
  series: string;
  value: number;
  /** Populated only when the request used <c>breakoutByFacility: true</c>. May be string or number over JSON. */
  facilityId?: string | number | null;
  /**
   * Optional Σ(FICO×balance) and Σ(balance) for the period (WA FICO: FICO in 300–850 only, matching registry).
   * Lets the grid compute a correct yearly WA as SUM(num)/SUM(den) over months, not SUM(monthly WA).
   */
  waFicoNumerator?: number;
  waFicoDenominator?: number;
  /** Optional Σ(APR×balance) and Σ(balance) for WA APR (decimal rate for % formatters). */
  waAprNumerator?: number;
  waAprDenominator?: number;
  /**
   * When set, `value` is numerator ÷ denominator for that month; denominator is
   * selected facilities’ total UPB (facility breakout), including loans with null
   * FICO/state etc., so visible shares need not sum to 100%.
   * Grid year rows use the **mean** of monthly `value` (see `meanMonthlyYearAgg`).
   */
  portfolioShareNumerator?: number;
  portfolioShareDenominator?: number;
  /** Optional paired metric for enriched tooltips (e.g. commitment alongside funded). */
  companionValue?: number;
};

export type ChartQueryMeta = {
  dataSource: string;
  metric: string;
  xAxisField: string;
  grain: string;
  seriesField: string;
};

export type ChartQueryResponse = {
  data: ChartDataPoint[];
  meta: ChartQueryMeta;
};

const CHART_QUERY_PATH = 'charts/query';
const CHART_FACILITIES_PATH = 'charts/facilities';
const CHART_AS_OF_DATES_PATH = 'charts/as-of-dates';

export type FacilityOption = {
  facilityId: string;
  facilityName: string;
};

export type DataTapeTrackerCell = {
  facilityId: string;
  facilityName: string;
  /** Month bucket as YYYY-MM (sortable as string). */
  asOfMonth: string;
  /** Human label, e.g. "Aug 24". */
  monthLabel: string;
  /** 1 if the facility has any loan-tape rows that month, 0 otherwise. */
  hasData: number;
  /** Earliest actual date within the month when hasData is 1. */
  sampleAsOfDate: string | null;
  /** True when CLS reports no active facility status for this facility id × month. */
  isTerminated?: boolean;
};

const CHART_DATA_TAPE_TRACKER_PATH = 'charts/data-tape-tracker';

/** Latest-month row from `GET /api/charts/dashboard-kpi?dataSource=…` (camelCase JSON; metrics align with chart SQL). */
export type AutoDashboardKpiLatest = {
  reportYear: number;
  reportMonthNum: number;
  reportMonthLabel: string;
  /**
   * Tape outstanding / balance KPI. For Energy C & I this is
   * <c>SUM(BORROWING_BASE_AMOUNT_CURRENT)</c> (dashboard headline card).
   */
  totalUpb: number;
  dpd30Upb: number | null;
  pct30PlusDpd: number | null;
  waApr: number | null;
  waFico: number | null;
  /** Energy C & I only: SUM(COMMITMENT_AMOUNT) at the latest reporting date. */
  totalCommitment?: number | null;
  /** Energy C & I only: SUM(TOTAL_USED) / SUM(COMMITMENT_AMOUNT) at the latest reporting date (0–1 ratio). */
  utilization?: number | null;
  /** Energy C & I only: SUM(BORROWING_BASE_AMOUNT_CURRENT) / SUM(ADJUSTED_DISCOUNTED_SOLAR_ASSET_BALANCE_CURRENT) (0–1 ratio). */
  borrowingBaseCoverage?: number | null;
  /** Energy C & I only: SUM(PROJECT_CAPACITY) at the latest reporting date (kW in tape; KPI displays MW). */
  projectCapacity?: number | null;
};

const CHART_TAPE_DASHBOARD_KPI_PATH = 'charts/dashboard-kpi';

/** HTTP client for chart query, facilities, KPI, and data-tape tracker endpoints. */
export class ChartQueryService extends BaseService {
  constructor() {
    super('/api/digitalconnect-service');
  }

  fetchChartQuery = async (request: ChartQueryRequest): Promise<ChartQueryResponse> => {
    // Keep raw series identity for stable facility colors across charts; chart UI formats legend labels.
    return this.post<ChartQueryRequest, ChartQueryResponse>({ path: CHART_QUERY_PATH, body: request });
  };

  fetchChartFacilities = async (dataSource: string) => {
    const response = await this.get({ path: CHART_FACILITIES_PATH, params: { dataSource } });
    return Array.isArray(response) ? response : [];
  };

  /** Distinct AS_OF_DATE values for allowlisted tape Dashboard As Of Date pickers. */
  fetchChartAsOfDates = async (dataSource: string) => {
    const response = await this.get({
      path: CHART_AS_OF_DATES_PATH,
      params: { dataSource },
    });
    if (Array.isArray(response)) {
      return response.filter((x): x is string => typeof x === 'string');
    }
    if (response && typeof response === 'object') {
      const body = response as { asOfDates?: string[]; AsOfDates?: string[] };
      const raw = body.asOfDates ?? body.AsOfDates;
      if (Array.isArray(raw)) {
        return raw.filter((x): x is string => typeof x === 'string');
      }
    }
    return [];
  };

  /** Latest-month (or selected as-of) KPI row for an allowlisted chart `dataSource`. */
  fetchTapeDashboardKpiLatest = async (dataSource: string, facilityIds?: readonly string[], asOfDate?: string) => {
    const params: Record<string, string> = { dataSource };
    if (facilityIds && facilityIds.length > 0) {
      params.facilityIds = facilityIds.join(',');
    }
    const trimmedAsOf = asOfDate?.trim();
    if (trimmedAsOf) {
      params.asOfDate = trimmedAsOf;
    }
    const raw = await this.get({ path: CHART_TAPE_DASHBOARD_KPI_PATH, params: params });

    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      const text = await raw.text();
      if (!text.trim()) {
        return null;
      }
      return JSON.parse(text) as AutoDashboardKpiLatest | null;
    }

    if (raw === null || raw === undefined) {
      return null;
    }
    return raw as AutoDashboardKpiLatest;
  };

  /** @deprecated Prefer {@link ChartQueryService.fetchTapeDashboardKpiLatest} with `dataSource: 'auto'`. */
  fetchAutoDashboardKpiLatest = async (facilityIds?: readonly string[]) => {
    return this.fetchTapeDashboardKpiLatest('auto', facilityIds);
  };

  fetchDataTapeTracker = async (dataSource: string) => {
    const response = await this.get({ path: CHART_DATA_TAPE_TRACKER_PATH, params: { dataSource } });
    return Array.isArray(response) ? response : [];
  };
}
